/**
 * Phase 1.3 — Geführte Kachel-Flows (Vertragsprüfung, MRG, Mandatsannahme).
 * State-Machine analog contractChatFlow.ts — rein clientseitig, testbar.
 */

import { getLexcoreSnapshotInfo } from './lexcoreSnapshot';
import type { CreatePortalAkteInput } from './portalAkten';
import type { ArkoHubModeId } from './arkoHubModes';
import {
  buildMandatsAkteTitle,
  mandatsNextStep,
  mergeMandatsSlots,
  sanitizeMandantLabel,
  type MandatsannahmeSlots,
} from './parseMandatsSlots';

export type GuidedFlowId = Extract<
  ArkoHubModeId,
  'vertragspruefung' | 'mrg_mietrecht' | 'mandatsannahme'
>;

export type GuidedRiskCategory = 'kritisch' | 'hinweis' | 'ok';

export interface GuidedRiskFinding {
  category: GuidedRiskCategory;
  title: string;
  detail: string;
}

export interface GuidedFlowChip {
  id: string;
  label: string;
  /** Wert, der als User-Antwort interpretiert wird */
  value: string;
}

export interface GuidedFlowBotBlock {
  text: string;
  chips?: GuidedFlowChip[];
  riskFindings?: GuidedRiskFinding[];
  laborDemo?: boolean;
  /** Confirm-Step Mandatsannahme: strukturierte Slot-Karte (kein Markdown der drei Felder). */
  mandatsConfirm?: MandatsannahmeSlots;
}

export interface GuidedFlowState {
  active: boolean;
  flowId: GuidedFlowId;
  step: string;
  data: {
    contractType?: string;
    mandant?: string;
    gegenstand?: string;
    dringlichkeit?: string;
    propertyType?: string;
    rentStart?: string;
    documentContext?: string;
    fileNames?: string[];
    akteId?: string;
    akteTitle?: string;
    /** Phase 1.4: Dokument wurde im Flow hochgeladen und ingestiert. */
    documentIngested?: boolean;
  };
  awaitingUpload?: boolean;
}

export const EMPTY_GUIDED_FLOW: GuidedFlowState = {
  active: false,
  flowId: 'vertragspruefung',
  step: '',
  data: {},
};

const CANCEL_RE = /\b(abbrechen|abbruch|stop(p)?|vergiss\s+es|doch\s+nicht)\b/i;

export function isGuidedFlowId(id: string): id is GuidedFlowId {
  return id === 'vertragspruefung' || id === 'mrg_mietrecht' || id === 'mandatsannahme';
}

export function guidedFlowTitle(flowId: GuidedFlowId): string {
  switch (flowId) {
    case 'vertragspruefung':
      return 'Geführte Vertragsprüfung';
    case 'mrg_mietrecht':
      return 'MRG-Mietrecht Schnellcheck';
    case 'mandatsannahme':
      return 'Mandatsannahme / Erstberatung';
    default:
      return 'Geführter Workflow';
  }
}

/** Heuristik: ARKO-Antwort in Ampel-Kategorien zerlegen. */
export function parseGuidedRiskFindings(text: string): GuidedRiskFinding[] {
  const findings: GuidedRiskFinding[] = [];
  const lines = text.split(/\n/).map((l) => l.trim()).filter(Boolean);

  const pushFinding = (category: GuidedRiskCategory, title: string, detail: string) => {
    if (!title.trim()) return;
    findings.push({ category, title: title.slice(0, 120), detail: detail.slice(0, 500) });
  };

  const categorizeLine = (line: string): GuidedRiskCategory | null => {
    if (/(?:kein\s+kritisch|nicht\s+kritisch)/i.test(line)) {
      /* fall through */
    } else if (
      /(?:^|\b)(?:kritisch|hohes?\s+risiko|warnung|achtung|unbedingt|dringend|fehlt|nicht\s+zulässig|rot\b)/i.test(
        line,
      )
    ) {
      return 'kritisch';
    }
    if (/(?:unbedenklich|unkritisch|in\s+ordnung|kein\s+risiko|\bok\b|passt|unauffällig|grün\b)/i.test(line)) {
      return 'ok';
    }
    if (/(?:hinweis|beachten|prüfen|empfehlung|sollte|könnte|optional|gelb\b)/i.test(line)) {
      return 'hinweis';
    }
    return null;
  };

  let sectionCategory: GuidedRiskCategory | null = null;

  for (const line of lines) {
    const header =
      /^(?:#{1,3}\s*)?(?:\*\*)?(kritisch|hinweis(?:e)?|unkritisch|ok|unbedenklich)(?:\*\*)?\s*:?\s*$/i.exec(
        line,
      );
    if (header) {
      const h = header[1].toLowerCase();
      if (h.startsWith('krit')) sectionCategory = 'kritisch';
      else if (h.startsWith('hinweis') || h === 'gelb') sectionCategory = 'hinweis';
      else sectionCategory = 'ok';
      continue;
    }

    const labeled =
      /^(?:\*\*)?(kritisch|hinweis|unkritisch|ok)\s*[:—–-]\s*(?:\*\*)?(.+)$/i.exec(line);
    if (labeled) {
      const catRaw = labeled[1].toLowerCase();
      const cat: GuidedRiskCategory = catRaw.startsWith('krit')
        ? 'kritisch'
        : catRaw.startsWith('hinweis')
          ? 'hinweis'
          : 'ok';
      const rest = labeled[2].trim();
      const colon = rest.indexOf(':');
      const title = colon > 0 ? rest.slice(0, colon) : rest.slice(0, 60);
      const detail = colon > 0 ? rest.slice(colon + 1).trim() : rest;
      pushFinding(cat, title, detail);
      continue;
    }

    if (/^[-*•]\s+/.test(line) || /^\d+[.)]\s+/.test(line)) {
      const cleaned = line.replace(/^[-*•]\s+/, '').replace(/^\d+[.)]\s+/, '');
      const colon = cleaned.indexOf(':');
      const title = colon > 0 ? cleaned.slice(0, colon) : cleaned.slice(0, 60);
      const detail = colon > 0 ? cleaned.slice(colon + 1).trim() : cleaned;
      const cat = categorizeLine(cleaned) ?? sectionCategory ?? 'hinweis';
      pushFinding(cat, title, detail);
      continue;
    }

    const inlineCat = categorizeLine(line);
    if (inlineCat && line.length > 20 && line.length < 280) {
      pushFinding(inlineCat, line.slice(0, 60), line);
    }
  }

  if (!findings.length && text.trim().length > 40) {
    // Prose ohne Bullets: 2–3 Sätze als Hinweise, damit das Ampel-Panel nicht leer wirkt.
    const sentences = text
      .replace(/\s+/g, ' ')
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 25);
    for (const s of sentences.slice(0, 3)) {
      const cat = categorizeLine(s) ?? 'hinweis';
      pushFinding(cat, s.slice(0, 70), s.slice(0, 400));
    }
    if (!findings.length) {
      pushFinding(
        'hinweis',
        'Zusammenfassung',
        text.trim().slice(0, 400) + (text.length > 400 ? '…' : ''),
      );
    }
  }

  return findings.slice(0, 8);
}

/** Labor-Stub wenn kein LEXCORE-Snapshot im Build. */
export function buildMrgLaborStubResponse(data: GuidedFlowState['data']): string {
  const lex = getLexcoreSnapshotInfo();
  const lexLine = lex
    ? `Rechtsstand ${lex.shortLabel} (${lex.packageLabel}) — verifizierte Normen mit [LEX]-Stempel möglich.`
    : 'Labor-Demo: LEXCORE-Snapshot nicht im Build — Orientierung ohne verifizierte Norm-Stempel.';

  return [
    '**MRG-Mietrecht Schnellcheck (Labor-Demo)**',
    '',
    lexLine,
    '',
    `Objekt: ${data.propertyType ?? '—'}`,
    `Mietbeginn: ${data.rentStart ?? '—'}`,
    '',
    '**Orientierung (keine Rechtsberatung):**',
    '- Wohnraum in Mehrfamilienhäusern vor 1945: MRG-Vollanwendung häufig — Kündigungsschutz, Mietzinsbeschränkung.',
    '- Gewerberaum: oft Teilanwendung oder Freistellung — Vertrag und Räumungsklauseln prüfen.',
    '- Pflichtschritte bei Streit: schriftliche Mahnung, Fristsetzung, Dokumentation.',
    '',
    'Für verbindliche Einschätzung: Akte anlegen und RIS/LEXCORE-Pfad in Produktion nutzen.',
  ].join('\n');
}

export interface GuidedFlowTurnResult {
  state: GuidedFlowState;
  blocks: GuidedFlowBotBlock[];
  /** User-Nachricht für Webhook (wenn gesetzt, ruft InlineChat ARKO auf). */
  webhook?: {
    chatInput: string;
    intent?: 'rechtsfrage' | 'erklaeren';
    documentContext?: string;
  };
  createAkte?: CreatePortalAkteInput;
  /** Phase 1.4: Hochgeladenes Dokument im Hub/Editor öffnen (Thomas-Fix). */
  openIngestedDocument?: boolean;
  /** Phase 1.4: Analyse abgeschlossen — documentAnalyzeDone setzen. */
  markDocumentAnalyzed?: boolean;
  ended?: boolean;
}

function cancelResult(state: GuidedFlowState): GuidedFlowTurnResult {
  return {
    state: EMPTY_GUIDED_FLOW,
    blocks: [{ text: 'Workflow abgebrochen. Sie können jederzeit eine Kachel erneut starten.' }],
    ended: true,
  };
}

function chips(...items: Array<[string, string]>): GuidedFlowChip[] {
  return items.map(([id, label]) => ({ id, label, value: label }));
}

const GUIDED_DISCLAIMER =
  '_Orientierung/Entwurf, keine Rechtsberatung (§ 9 RAO). Jederzeit „abbrechen" möglich._';

const RISK_FORMAT_HINT =
  'Antworte mit **strukturierter Risikoeinschätzung** als Bullet-Liste im Format:\n' +
  '- Kritisch: <Klausel/Thema>: <kurze Begründung>\n' +
  '- Hinweis: <Thema>: <kurze Begründung>\n' +
  '- Unkritisch: <Thema>: <kurze Begründung>\n' +
  'Nutze echte [LEX]-Normen nur wenn verifiziert; sonst klar als Orientierung kennzeichnen.';

/** Startet einen geführten Flow — erste Bot-Nachricht(en). */
export function startGuidedFlow(flowId: GuidedFlowId): GuidedFlowTurnResult {
  switch (flowId) {
    case 'vertragspruefung':
      return {
        state: { active: true, flowId, step: 'await_upload', data: {}, awaitingUpload: true },
        blocks: [
          {
            text:
              '**Geführte Vertragsprüfung** — Schritt 1/3: Vertragstyp oder Datei.\n\n' +
              'Laden Sie den Vertrag unter **Dokumente** hoch, ziehen Sie die Datei in den Chat, ' +
              'oder wählen Sie einen Typ und fahren Sie ohne Dokument fort.\n\n' +
              GUIDED_DISCLAIMER,
            chips: chips(
              ['ohne', 'Ohne Dokument fortfahren'],
              ['nda', 'NDA / Geheimhaltung'],
              ['dienst', 'Dienstleistungsvertrag'],
              ['miete', 'Mietvertrag'],
            ),
          },
        ],
      };

    case 'mrg_mietrecht': {
      const lex = getLexcoreSnapshotInfo();
      const lexHint = lex
        ? `\n\nLEXCORE-Rechtsstand **${lex.shortLabel}** — Antworten können [LEX]-Stempel tragen.`
        : '\n\n_Labor-Hinweis: Ohne LEXCORE-Snapshot im Build — Orientierung **ohne** verifizierten Norm-Stempel (Content = LEXCORE-Ball)._';
      return {
        state: { active: true, flowId, step: 'property_type', data: {} },
        blocks: [
          {
            text:
              '**MRG-Mietrecht Schnellcheck** — Schritt 1/3: Objektart.\n\n' +
              'Welche Art von Objekt betrifft Ihre Frage?\n\n' +
              GUIDED_DISCLAIMER +
              lexHint,
            chips: chips(
              ['wohn', 'Wohnraum'],
              ['gewerbe', 'Gewerberaum'],
              ['gemischt', 'Gemischt genutzt'],
            ),
            laborDemo: !lex,
          },
        ],
      };
    }

    case 'mandatsannahme':
      return {
        state: { active: true, flowId, step: 'mandant', data: {} },
        blocks: [
          {
            text:
              '**Mandatsannahme / Erstberatung** — Schritt 1/4: Mandant.\n\n' +
              'Kurzer Name **oder** ein Satz mit Name, Gegenstand und Dringlichkeit ' +
              '(z. B. „Muster GmbH, Kündigung, dringend“). Nicht den ganzen Satz als Mandantenname.\n\n' +
              GUIDED_DISCLAIMER,
          },
        ],
      };

    default:
      return { state: EMPTY_GUIDED_FLOW, blocks: [{ text: 'Unbekannter Workflow.' }], ended: true };
  }
}

/** Nächster Schritt nach User-Eingabe oder Chip-Wert. */
export function advanceGuidedFlow(
  state: GuidedFlowState,
  userText: string,
  options?: { documentContext?: string; fileNames?: string[] },
): GuidedFlowTurnResult {
  if (!state.active) {
    return { state: EMPTY_GUIDED_FLOW, blocks: [], ended: true };
  }

  const text = userText.trim();
  if (CANCEL_RE.test(text)) return cancelResult(state);

  const data = { ...state.data };
  if (options?.documentContext) data.documentContext = options.documentContext;
  if (options?.fileNames?.length) data.fileNames = options.fileNames;

  switch (state.flowId) {
    case 'vertragspruefung':
      return advanceVertragspruefung(state, text, data);
    case 'mrg_mietrecht':
      return advanceMrgMietrecht(state, text, data);
    case 'mandatsannahme':
      return advanceMandatsannahme(state, text, data);
    default:
      return cancelResult(state);
  }
}

function advanceVertragspruefung(
  state: GuidedFlowState,
  text: string,
  data: GuidedFlowState['data'],
): GuidedFlowTurnResult {
  switch (state.step) {
    case 'contract_type':
      data.contractType = text;
      if (data.documentContext) {
        return startVertragsAnalyse(state, data, text);
      }
      return {
        state: { ...state, step: 'await_upload', data, awaitingUpload: true },
        blocks: [
          {
            text:
              `Vertragstyp: **${text}**\n\n` +
              'Bitte Vertrag als Datei anhängen oder „Ohne Dokument fortfahren" wählen.',
            chips: chips(['ohne', 'Ohne Dokument fortfahren']),
          },
        ],
      };

    case 'document':
      if (/hochladen|anhang|upload|datei/i.test(text)) {
        return {
          state: { ...state, step: 'await_upload', data, awaitingUpload: true },
          blocks: [
            {
              text:
                'Bitte **Datei anhängen** (PDF, DOCX …) und optional eine kurze Notiz senden — ' +
                'oder „Ohne Dokument" wählen.',
              chips: chips(['ohne', 'Ohne Dokument fortfahren']),
            },
          ],
        };
      }
      return startVertragsAnalyse(state, data, text);

    case 'await_upload': {
      const contractChip =
        /nda|geheimhaltung/i.test(text)
          ? 'NDA / Geheimhaltung'
          : /dienstleistung/i.test(text)
            ? 'Dienstleistungsvertrag'
            : /mietvertrag|miete/i.test(text)
              ? 'Mietvertrag'
              : null;
      if (contractChip) {
        data.contractType = contractChip;
      }
      if (/ohne\s+dokument/i.test(text) && !data.documentContext) {
        if (!data.contractType) {
          return {
            state: { ...state, step: 'contract_type', data, awaitingUpload: false },
            blocks: [
              {
                text: 'Welchen **Vertragstyp** möchten Sie prüfen?',
                chips: chips(
                  ['nda', 'NDA / Geheimhaltung'],
                  ['dienst', 'Dienstleistungsvertrag'],
                  ['miete', 'Mietvertrag'],
                  ['sonst', 'Sonstiger Vertrag'],
                ),
              },
            ],
          };
        }
        return startVertragsAnalyse(state, data, '');
      }
      if (data.documentContext) {
        if (!data.contractType) {
          return {
            state: { ...state, step: 'contract_type', data, awaitingUpload: false },
            blocks: [
              {
                text: 'Dokument erkannt. Welchen **Vertragstyp** soll ich prüfen?',
                chips: chips(
                  ['nda', 'NDA / Geheimhaltung'],
                  ['dienst', 'Dienstleistungsvertrag'],
                  ['miete', 'Mietvertrag'],
                  ['sonst', 'Sonstiger Vertrag'],
                ),
              },
            ],
          };
        }
        return startVertragsAnalyse(state, data, text);
      }
      if (contractChip) {
        return {
          state: { ...state, step: 'await_upload', data, awaitingUpload: true },
          blocks: [
            {
              text: `Vertragstyp: **${contractChip}** — bitte Datei anhängen oder „Ohne Dokument fortfahren".`,
              chips: chips(['ohne', 'Ohne Dokument fortfahren']),
            },
          ],
        };
      }
      return {
        state: { ...state, step: 'await_upload', data, awaitingUpload: true },
        blocks: [
          {
            text: 'Noch kein Anhang erkannt — bitte Datei anhängen, Vertragstyp wählen oder „Ohne Dokument fortfahren".',
            chips: chips(
              ['ohne', 'Ohne Dokument fortfahren'],
              ['nda', 'NDA / Geheimhaltung'],
              ['dienst', 'Dienstleistungsvertrag'],
            ),
          },
        ],
      };
    }

    case 'analyzing':
      return handleVertragsResult(state, data, text);

    case 'akte_choice':
      return handleAkteChoice(state, data, text, 'vertrag_beratung');

    default:
      return cancelResult(state);
  }
}

function startVertragsAnalyse(
  state: GuidedFlowState,
  data: GuidedFlowState['data'],
  userNote: string,
): GuidedFlowTurnResult {
  const typ = data.contractType ?? 'Vertrag';
  const docHint = data.documentContext
    ? 'mit angehängtem Vertragstext'
    : 'auf Basis Ihrer Angaben (ohne Dokument)';
  const chatInput =
    `Geführte Vertragsprüfung (${typ}) ${docHint}:\n` +
    `${RISK_FORMAT_HINT}\n` +
    'Schwerpunkte: Haftung, Laufzeit, Kündigung, Gerichtsstand, einseitige Klauseln.\n' +
    (userNote ? `\nZusatz vom Anwalt: ${userNote}` : '');

  return {
    state: { ...state, step: 'analyzing', data, awaitingUpload: false },
    blocks: [{ text: 'Vertragsprüfung läuft — ARKO analysiert die Klauseln und baut die Risiko-Ampel…' }],
    webhook: {
      chatInput,
      intent: 'erklaeren',
      documentContext: data.documentContext,
    },
  };
}

function handleVertragsResult(
  state: GuidedFlowState,
  data: GuidedFlowState['data'],
  arkoReply: string,
): GuidedFlowTurnResult {
  const findings = parseGuidedRiskFindings(arkoReply);
  const hasDoc = Boolean(data.documentContext?.trim() || data.fileNames?.length);
  const akteChips: Array<[string, string]> = [
    ['neu', 'Neue Akte anlegen'],
    ['fertig', 'Später — Workflow beenden'],
  ];
  if (hasDoc) {
    akteChips.unshift(['open_editor', 'Dokument im Editor öffnen']);
  }
  return {
    state: { ...state, step: 'akte_choice', data: { ...data, documentIngested: hasDoc || data.documentIngested } },
    blocks: [
      { text: arkoReply, riskFindings: findings.length ? findings : undefined },
      {
        text: hasDoc
          ? '**Nächster Schritt:** Dokument im Editor prüfen, Akte anlegen oder Workflow beenden?'
          : '**Nächster Schritt:** Akte Vertrag/Beratung anlegen oder mit bestehender verknüpfen?',
        chips: chips(...akteChips),
      },
    ],
    markDocumentAnalyzed: hasDoc,
  };
}

function advanceMrgMietrecht(
  state: GuidedFlowState,
  text: string,
  data: GuidedFlowState['data'],
): GuidedFlowTurnResult {
  switch (state.step) {
    case 'property_type':
      data.propertyType = text;
      return {
        state: { ...state, step: 'rent_start', data },
        blocks: [
          {
            text: `Objektart: **${text}**\n\nWann begann (oder beginnt) das Mietverhältnis? (Datum oder „unbekannt")`,
          },
        ],
      };

    case 'rent_start':
      data.rentStart = text;
      return {
        state: { ...state, step: 'main_question', data },
        blocks: [
          {
            text:
              'Was ist Ihre **konkrete Frage** zum MRG (z. B. Kündigung, Mietzins, Vollanwendung)?',
          },
        ],
      };

    case 'main_question': {
      const lex = getLexcoreSnapshotInfo();
      if (!lex) {
        const stub = buildMrgLaborStubResponse({ ...data, rentStart: data.rentStart });
        const findings = parseGuidedRiskFindings(stub);
        return {
          state: { ...state, step: 'akte_choice', data },
          blocks: [
            {
              text: stub,
              riskFindings: findings,
              laborDemo: true,
            },
            {
              text:
                'Labor ohne LEXCORE-Snapshot — **kein [LEX]-Stempel**. ' +
                'Content-Ball liegt bei LEXCORE. Optional: Akte anlegen oder Workflow beenden.',
              chips: chips(
                ['neu', 'Neue Akte anlegen'],
                ['fertig', 'Später — Workflow beenden'],
              ),
            },
          ],
        };
      }
      const chatInput =
        `MRG-Mietrecht Schnellcheck (Österreich):\n` +
        `Objekt: ${data.propertyType}\nMietbeginn: ${data.rentStart}\n\n` +
        `Frage: ${text}\n\n` +
        'Prüfe Voll-/Teilanwendung des MRG und Pflichtschritte.\n' +
        `${RISK_FORMAT_HINT}\n` +
        'Nenne relevante Normen nur mit [LEX]-Basis wenn verifiziert.';

      return {
        state: { ...state, step: 'analyzing', data },
        blocks: [{ text: 'MRG-Schnellcheck — verifizierte Normen werden abgerufen…' }],
        webhook: { chatInput, intent: 'rechtsfrage' },
      };
    }

    case 'analyzing':
      return handleMrgResult(state, data, text);

    case 'akte_choice':
      return handleAkteChoice(state, data, text, 'vertrag_beratung');

    default:
      return cancelResult(state);
  }
}

function handleMrgResult(
  state: GuidedFlowState,
  data: GuidedFlowState['data'],
  arkoReply: string,
): GuidedFlowTurnResult {
  const findings = parseGuidedRiskFindings(arkoReply);
  return {
    state: { ...state, step: 'akte_choice', data },
    blocks: [
      { text: arkoReply, riskFindings: findings.length ? findings : undefined },
      {
        text: '**Nächster Schritt:** Akte Mietrecht anlegen oder Workflow beenden?',
        chips: chips(
          ['neu', 'Neue Akte anlegen'],
          ['fertig', 'Später — Workflow beenden'],
        ),
      },
    ],
  };
}

function summarizeMandatsData(data: GuidedFlowState['data']): string {
  const lines = [`Mandant: **${data.mandant ?? '—'}**`];
  if (data.gegenstand) lines.push(`Gegenstand: **${data.gegenstand}**`);
  if (data.dringlichkeit) lines.push(`Dringlichkeit: **${data.dringlichkeit}**`);
  return lines.join('\n');
}

const MANDATS_CONFIRM_HINT =
  'Vor der Akte Name und Gegenstand prüfen. Optional Dokument anhängen — oder direkt anlegen.';

function mandatsConfirmBlock(data: GuidedFlowState['data']): GuidedFlowBotBlock {
  return {
    text: MANDATS_CONFIRM_HINT,
    chips: chips(['skip', 'Akte jetzt anlegen']),
    mandatsConfirm: {
      mandant: data.mandant,
      gegenstand: data.gegenstand,
      dringlichkeit: data.dringlichkeit,
    },
  };
}

function dringlichkeitChips(): GuidedFlowChip[] {
  return chips(
    ['niedrig', 'Niedrig — Routine'],
    ['mittel', 'Mittel — zeitnah'],
    ['hoch', 'Hoch — dringend'],
  );
}

function advanceMandatsannahme(
  state: GuidedFlowState,
  text: string,
  data: GuidedFlowState['data'],
): GuidedFlowTurnResult {
  switch (state.step) {
    case 'mandant':
    case 'gegenstand':
    case 'dringlichkeit': {
      const merged = mergeMandatsSlots(state.step, text, data);
      data.mandant = merged.mandant;
      data.gegenstand = merged.gegenstand;
      data.dringlichkeit = merged.dringlichkeit;
      const next = mandatsNextStep(data);

      if (next === 'upload_optional') {
        return {
          state: { ...state, step: 'upload_optional', data, awaitingUpload: true },
          blocks: [mandatsConfirmBlock(data)],
        };
      }

      if (next === 'dringlichkeit') {
        return {
          state: { ...state, step: 'dringlichkeit', data },
          blocks: [
            {
              text: `${summarizeMandatsData(data)}\n\nWie **dringlich** ist die Angelegenheit?`,
              chips: dringlichkeitChips(),
            },
          ],
        };
      }

      return {
        state: { ...state, step: 'gegenstand', data },
        blocks: [
          {
            text: `Mandant: **${data.mandant}**\n\nWas ist der **Gegenstand** / das Anliegen?`,
          },
        ],
      };
    }

    case 'upload_optional':
      if (data.documentContext || /akte\s+jetzt|überspringen|skip|ohne/i.test(text)) {
        return createMandatsAkte(state, data);
      }
      return {
        state: { ...state, step: 'upload_optional', data, awaitingUpload: true },
        blocks: [
          {
            ...mandatsConfirmBlock(data),
            text: 'Bitte Dokument anhängen oder „Akte jetzt anlegen" wählen.',
          },
        ],
      };

    default:
      return cancelResult(state);
  }
}

function createMandatsAkte(
  state: GuidedFlowState,
  data: GuidedFlowState['data'],
): GuidedFlowTurnResult {
  const title = buildMandatsAkteTitle({
    mandant: data.mandant,
    gegenstand: data.gegenstand,
  });
  data.akteTitle = title;
  data.mandant = sanitizeMandantLabel(data.mandant ?? '') || data.mandant;

  return {
    state: { ...state, step: 'done', data, awaitingUpload: false },
    blocks: [
      {
        text:
          `Ich lege die Akte **${title}** an und starte die Checkliste Vertrag/Beratung…`,
      },
    ],
    createAkte: {
      title,
      mandant_label: data.mandant,
      gegenseite_label: undefined,
      vertragstyp: data.gegenstand?.slice(0, 80),
      matter_type: 'vertrag_beratung',
      template_id: 'vertrag-beratung',
    },
    ended: true,
  };
}

function handleAkteChoice(
  state: GuidedFlowState,
  data: GuidedFlowState['data'],
  text: string,
  matterType: string,
): GuidedFlowTurnResult {
  if (/editor\s+öffnen|im\s+editor|dokument\s+öffnen|open_editor/i.test(text)) {
    return {
      state: { ...state, step: 'done', data },
      blocks: [
        {
          text: 'Dokument wird im Editor geöffnet — Prüfungsergebnis bleibt im Chat. Aktenwächter finden Sie im Dokumenten-Hub.',
        },
      ],
      openIngestedDocument: true,
      ended: true,
    };
  }

  if (/neu|anlegen|erstellen/i.test(text)) {
    const titleBase =
      state.flowId === 'mrg_mietrecht'
        ? `MRG — ${data.propertyType ?? 'Mietrecht'}`
        : `Vertrag — ${data.contractType ?? 'Prüfung'}`;
    const title = titleBase.slice(0, 120);
    return {
      state: { ...state, step: 'done', data: { ...data, akteTitle: title } },
      blocks: [{ text: `Akte **${title}** wird angelegt…` }],
      createAkte: {
        title,
        mandant_label: data.mandant?.trim() || undefined,
        vertragstyp: data.contractType ?? data.propertyType,
        matter_type: matterType,
        template_id: 'vertrag-beratung',
      },
      ended: true,
    };
  }

  return {
    state: { ...state, step: 'done', data },
    blocks: [
      {
        text:
          state.flowId === 'mrg_mietrecht'
            ? 'MRG-Schnellcheck abgeschlossen. Aktenwächter finden Sie im Dokumenten-Hub.'
            : 'Vertragsprüfung abgeschlossen. Aktenwächter finden Sie im Dokumenten-Hub.',
      },
    ],
    ended: true,
  };
}

/** Nach Webhook-Antwort: Flow fortsetzen (nur analyzing-Schritte). */
export function continueGuidedFlowAfterWebhook(
  state: GuidedFlowState,
  arkoReply: string,
): GuidedFlowTurnResult {
  if (!state.active || state.step !== 'analyzing') {
    return { state, blocks: [], ended: false };
  }

  if (state.flowId === 'vertragspruefung') {
    return handleVertragsResult(state, state.data, arkoReply);
  }

  if (state.flowId === 'mrg_mietrecht') {
    return handleMrgResult(state, state.data, arkoReply);
  }

  return { state: EMPTY_GUIDED_FLOW, blocks: [{ text: arkoReply }], ended: true };
}
